import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Button from "shared/Button";
import Alert from "shared/Alert";
import FilterChip from "shared/FilterChip";
import StatusPill from "shared/StatusPill";
import { TABLE_SHELL, THEAD_ROW, TH, TBODY_ROW, TD } from "shared/tableStyles";
import { fetchEvaluationOverview } from "../api/evaluate";
import type { EvaluationOverview } from "../modal/evaluate_context";
import { CRITERIA, evaluationAverage, formatDate, formatDaysAgo, formatScore, scoreTone, shiftWeek } from "./evaluationConfig";
import { TONE_SOFT } from "shared/tones";
import { cn } from "lib/utils";

const SHORT: Record<string, string> = {
  task_completion: "Tasks", team_player: "Team", time_management: "Time", positive_work_attitide: "Attitude",
  adaptable_and_flexible: "Adapt", ability_to_learn: "Learn", problem_solving: "Problems", punctuality: "Punctual",
};

interface Props {
  /** Open this employee's evaluations in the Evaluate tab. */
  onOpenEmployee: (id: number) => void;
  /** Start with only the not-evaluated-recently employees (the banner's Show link). */
  initialOverdueOnly?: boolean;
}

/** Everyone's scores for one Monday-to-Sunday week side by side, plus how long since each was last evaluated. */
export default function WeekMatrix({ onOpenEmployee, initialOverdueOnly = false }: Props) {
  const [week, setWeek] = useState<string | undefined>(undefined);
  const [data, setData] = useState<EvaluationOverview | null>(null);
  const [error, setError] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(initialOverdueOnly);

  useEffect(() => {
    let cancelled = false;
    setError("");
    fetchEvaluationOverview(week)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError("Unable to load the weekly overview."); });
    return () => { cancelled = true; };
  }, [week]);

  const rows = useMemo(
    () => (data?.employees ?? []).filter((e) => !overdueOnly || e.tier !== "ok"),
    [data, overdueOnly],
  );
  const overdueCount = (data?.employees ?? []).filter((e) => e.tier !== "ok").length;
  const evaluatedCount = (data?.employees ?? []).filter((e) => e.evaluation).length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" aria-label="Previous week" onClick={() => data && setWeek(shiftWeek(data.week_start, -1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-2 text-sm font-medium text-foreground min-w-[13rem] text-center">
            {data ? `${formatDate(data.week_start)} – ${formatDate(data.week_end)}` : "Loading…"}
          </span>
          <Button variant="outline" size="sm" aria-label="Next week" onClick={() => data && setWeek(shiftWeek(data.week_start, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={() => setWeek(undefined)}>This week</Button>
        {data && <span className="text-xs text-muted-foreground">{evaluatedCount} of {data.employees.length} evaluated this week</span>}
        <div className="ml-auto">
          <FilterChip active={overdueOnly} onClick={() => setOverdueOnly((v) => !v)} count={overdueCount}>
            Not evaluated in {data?.overdue_days ?? 14}+ days
          </FilterChip>
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <div className={cn(TABLE_SHELL, "overflow-x-auto")}>
        <table className="w-full text-sm">
          <thead>
            <tr className={THEAD_ROW}>
              <th className={TH}>Employee</th>
              {CRITERIA.map((c) => <th key={c.key} className={cn(TH, "text-center")} title={c.label}>{SHORT[c.key]}</th>)}
              <th className={cn(TH, "text-center")}>Average</th>
              <th className={TH}>Last evaluated</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && data && (
              <tr><td colSpan={CRITERIA.length + 3} className="px-4 py-10 text-center text-sm text-muted-foreground">
                {overdueOnly ? "Everyone has been evaluated recently." : "No employees in your scope."}
              </td></tr>
            )}
            {rows.map((e) => {
              const avg = e.evaluation ? evaluationAverage(e.evaluation) : null;
              return (
                <tr key={e.id} className={TBODY_ROW}>
                  <td className={TD}>
                    <button type="button" onClick={() => onOpenEmployee(e.id)} className="text-left hover:underline">
                      <span className="block font-medium text-foreground">{e.name}</span>
                      <span className="block text-xs text-muted-foreground">{e.designation || e.employee_code}</span>
                    </button>
                  </td>
                  {CRITERIA.map((c) => {
                    const v = e.evaluation ? e.evaluation[c.key] : 0;
                    return (
                      <td key={c.key} className={cn(TD, "text-center")}>
                        {v > 0
                          ? <span className={cn("inline-block min-w-[2rem] rounded-md border px-1.5 py-0.5 text-xs font-medium", TONE_SOFT[scoreTone(v)])}>{formatScore(v)}</span>
                          : <span className="text-muted-foreground">–</span>}
                      </td>
                    );
                  })}
                  <td className={cn(TD, "text-center font-semibold")}>{avg !== null ? formatScore(avg) : <span className="text-muted-foreground font-normal">–</span>}</td>
                  <td className={TD}>
                    <span className="text-xs text-muted-foreground mr-2">{e.last_evaluated ? formatDate(e.last_evaluated) : ""}</span>
                    {e.tier === "never" && <StatusPill tone="danger" size="sm">Never evaluated</StatusPill>}
                    {e.tier === "stale" && <StatusPill tone="warning" size="sm">{formatDaysAgo(e.days_since)}</StatusPill>}
                    {e.tier === "ok" && <span className="text-xs text-muted-foreground">{formatDaysAgo(e.days_since)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
